import React, { useEffect, useRef, useState } from 'react';

// ── CONSAM DMS — Update Banner ─────────────────────────────────────────────
// Polls /api/health every 5 minutes. If the bundle hash changes since page
// load, shows a non-intrusive banner prompting the user to reload.
// No forced reload — user finishes what they're doing and clicks when ready.
// ─────────────────────────────────────────────────────────────────────────

const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

export default function UpdateBanner() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const initialHash = useRef(null);

  useEffect(() => {
    // Fetch once immediately to record the hash at page-load time
    fetchHash().then(hash => {
      if (hash) initialHash.current = hash;
    });

    // Then poll every 5 minutes
    const timer = setInterval(async () => {
      const hash = await fetchHash();
      if (hash && initialHash.current && hash !== initialHash.current) {
        setUpdateAvailable(true);
        clearInterval(timer); // stop polling once we know there's an update
      }
    }, POLL_INTERVAL);

    return () => clearInterval(timer);
  }, []);

  if (!updateAvailable) return null;

  return (
    <div style={styles.banner}>
      <span style={styles.icon}>🔄</span>
      <span style={styles.text}>
        CONSAM DMS has been updated — reload to get the latest version.
      </span>
      <button
        style={styles.button}
        onClick={() => window.location.reload()}
      >
        Reload now
      </button>
      <button
        style={styles.dismiss}
        onClick={() => setUpdateAvailable(false)}
        title="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}

async function fetchHash() {
  try {
    const res = await fetch('/api/health', { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return data.bundle || null;
  } catch {
    return null;
  }
}

const styles = {
  banner: {
    position: 'fixed',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: '#1a3c5e',
    color: '#fff',
    padding: '12px 20px',
    borderRadius: '8px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
    fontSize: '14px',
    whiteSpace: 'nowrap',
    maxWidth: '90vw',
  },
  icon: {
    fontSize: '16px',
    flexShrink: 0,
  },
  text: {
    flex: 1,
    whiteSpace: 'normal',
    lineHeight: '1.4',
  },
  button: {
    background: '#2d7dd2',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    padding: '6px 14px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '13px',
    flexShrink: 0,
  },
  dismiss: {
    background: 'transparent',
    color: '#aac4e0',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '2px 4px',
    flexShrink: 0,
    lineHeight: 1,
  },
};
